import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Eye, EyeOff, Heart, CheckCircle2, ArrowRight, ChevronDown, ChevronUp, Mail, RefreshCw } from 'lucide-react';
import { processPendingDeeplink } from './DeeplinkPage';
import { getDefaultLandingPath } from '../config/domainEntry';
import axios from 'axios';
import { BACKEND_URL } from 'apiConfig';

const API_URL = BACKEND_URL;
const MEMBERSHIP_KEY = 'lp_seen_membership_compare';

/* Compact membership comparison shown once on login page */
const MembershipCompact = () => (
  <div className="bg-gradient-to-b from-sky-50 to-white rounded-2xl border border-sky-100 p-5 space-y-3">
    <p className="text-center text-sm font-semibold text-sky-800">
      All new users get a <strong>30-day Pro trial</strong> with full access
    </p>
    <div className="grid grid-cols-3 gap-2 text-center text-xs">
      <div className="bg-white rounded-xl border border-slate-200 p-3">
        <p className="font-bold text-slate-800 mb-1">Free</p>
        <p className="text-slate-500">1 digest</p>
        <p className="text-slate-500">Read-only community</p>
      </div>
      <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-3">
        <p className="font-bold text-emerald-800 mb-1">Verified</p>
        <p className="text-emerald-700">3 digests</p>
        <p className="text-emerald-700">Community access</p>
      </div>
      <div className="bg-sky-50 rounded-xl border border-sky-300 p-3 ring-1 ring-sky-200">
        <p className="font-bold text-sky-800 mb-1">Pro</p>
        <p className="text-sky-700">5 digests + Audio</p>
        <p className="text-sky-700">LitScholar AI</p>
      </div>
    </div>
    <Link to="/membership" className="block text-center text-xs text-sky-600 hover:underline">
      View full comparison <ArrowRight className="w-3 h-3 inline" />
    </Link>
  </div>
);

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState(null); // {code, message}
  const [showVerification, setShowVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // First-visit membership comparison
  const [showMembership, setShowMembership] = useState(() => {
    try { return !localStorage.getItem(MEMBERSHIP_KEY); } catch { return true; }
  });

  useEffect(() => {
    if (showMembership) {
      try { localStorage.setItem(MEMBERSHIP_KEY, '1'); } catch {}
    }
  }, [showMembership]);

  const handleVerifyCode = async () => {
    if (verificationCode.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }
    setVerifying(true);
    try {
      await axios.post(`${API_URL}/api/auth/verify-code`, null, {
        params: { email, code: verificationCode }
      });
      toast.success('Email verified! Logging you in...');
      setShowVerification(false);
      // Attempt login again now that email is verified
      await login(email, password);
      const next = searchParams.get('next');
      const isRelativePath = next && next.startsWith('/') && !next.startsWith('//');
      navigate(isRelativePath ? next : '/library');
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.error_code === 'invalid_code') {
        toast.error('Invalid code. Please check and try again.');
      } else if (detail?.error_code === 'code_expired') {
        toast.error('Code expired. Click "Resend Code" to get a new one.');
      } else {
        toast.error('Verification failed. Please try again.');
      }
    } finally {
      setVerifying(false);
    }
  };

  const handleResendCode = async () => {
    setResending(true);
    try {
      await axios.post(`${API_URL}/api/auth/resend-verification`, null, {
        params: { email }
      });
      toast.success('New verification code sent to your email!');
    } catch (error) {
      toast.error('Failed to resend code. Please try again.');
    } finally {
      setResending(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLoginError(null);

    try {
      const response = await login(email, password);
      
      // Check if user is not verified
      if (response?.user && !response.user.is_verified) {
        // User exists but not verified - show verification prompt
        setShowVerification(true);
        // Send a new verification code
        try {
          await axios.post(`${API_URL}/api/auth/resend-verification`, null, {
            params: { email }
          });
          toast.info('Please verify your email to continue. Check your inbox for the code.');
        } catch {
          toast.info('Please verify your email. Click "Resend Code" if needed.');
        }
        return;
      }
      
      toast.success('Login successful!');
      
      // Phase A v2: Check for pending deeplink action
      const hadPendingDeeplink = await processPendingDeeplink(navigate);
      if (!hadPendingDeeplink) {
      // Phase 3: honour ?next= param from ProtectedRoute redirect
      // Security: only accept relative paths; reject //evil.com, https://, javascript:, etc.
      const next = searchParams.get('next');
      const isRelativePath = next && next.startsWith('/') && !next.startsWith('//');
      const safePath = isRelativePath ? next : getDefaultLandingPath();
      navigate(safePath);
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      const errorCode = typeof detail === 'object' ? detail?.error_code : null;
      const errorMessage = typeof detail === 'object' ? detail?.message : (typeof detail === 'string' ? detail : null);

      if (errorCode === 'account_not_found') {
        setLoginError({ code: errorCode, message: errorMessage });
      } else if (errorCode === 'wrong_password') {
        setLoginError({ code: errorCode, message: errorMessage });
      } else {
        toast.error(errorMessage || 'Login failed. Please check your credentials.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Show verification code entry screen if user is not verified
  if (showVerification) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-8">
        <Card className="w-full max-w-md" data-testid="verification-required-card">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mb-4">
              <Mail className="h-6 w-6 text-amber-600" />
            </div>
            <CardTitle className="text-2xl">Email Verification Required</CardTitle>
            <CardDescription>Please verify your email to activate your account</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-600 text-center">
              We've sent a 6-digit code to <strong>{email}</strong>
            </p>
            
            <div className="space-y-2">
              <Label htmlFor="verificationCode">Enter Verification Code</Label>
              <Input
                id="verificationCode"
                type="text"
                placeholder="000000"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="text-center text-2xl font-mono tracking-[0.5em] h-14"
                maxLength={6}
                data-testid="verification-code-input"
              />
              <p className="text-xs text-gray-500 text-center">Code expires in 15 minutes</p>
            </div>

            <Button 
              onClick={handleVerifyCode} 
              className="w-full" 
              disabled={verifying || verificationCode.length !== 6}
              data-testid="verify-code-btn"
            >
              {verifying ? 'Verifying...' : 'Verify & Login'}
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-gray-500">Didn't receive it?</span>
              </div>
            </div>

            <Button 
              variant="outline" 
              onClick={handleResendCode} 
              className="w-full"
              disabled={resending}
              data-testid="resend-code-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${resending ? 'animate-spin' : ''}`} />
              {resending ? 'Sending...' : 'Resend Code'}
            </Button>

            <Button 
              variant="ghost" 
              onClick={() => { setShowVerification(false); setVerificationCode(''); }}
              className="w-full text-gray-500"
            >
              Back to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-8">
      <div className="w-full max-w-md space-y-5">
        {/* Membership comparison — first visit only */}
        {showMembership && <MembershipCompact />}
        {!showMembership && (
          <div className="text-center">
            <Link to="/membership" className="text-xs text-sky-600 hover:underline">
              View membership options <ArrowRight className="w-3 h-3 inline" />
            </Link>
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Welcome Back</CardTitle>
            <CardDescription>Sign in to your LitCurrent account</CardDescription>
          </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setLoginError(null); }}
                required
                data-testid="email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setLoginError(null); }}
                  required
                  data-testid="password-input"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            {loginError && (
              <div data-testid="login-error-banner" className="rounded-md border p-3 text-sm bg-red-50 border-red-200">
                {loginError.code === 'account_not_found' ? (
                  <p data-testid="error-account-not-found" className="text-red-700">
                    {loginError.message}{' '}
                    <Link to="/signup" className="font-semibold underline hover:text-red-900">
                      Sign up here
                    </Link>
                  </p>
                ) : loginError.code === 'wrong_password' ? (
                  <p data-testid="error-wrong-password" className="text-red-700">
                    {loginError.message}{' '}
                    <Link to="/request-password-reset" className="font-semibold underline hover:text-red-900">
                      Reset password
                    </Link>
                  </p>
                ) : (
                  <p className="text-red-700">{loginError.message}</p>
                )}
              </div>
            )}

            <div className="flex justify-end">
              <Link
                to="/request-password-reset"
                className="text-sm text-blue-600 hover:underline"
              >
                Forgot password?
              </Link>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={loading}
              data-testid="login-button"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm">
            <span className="text-gray-600">Don't have an account? </span>
            <Link to="/signup" className="text-blue-600 hover:underline font-medium">
              Sign up
            </Link>
          </div>
        </CardContent>
      </Card>
      </div>
    </div>
  );
};

export default LoginPage;